import { useSelector, useDispatch } from 'react-redux'
import { setUser, initialState } from 'store/auth/userSlice'
import { apiSignIn, apiSignOut, apiSignUp, apiInviteSignUp } from 'services/AuthService'
import { onSignInSuccess, onSignOutSuccess } from 'store/auth/sessionSlice'
import appConfig from 'configs/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'

function useAuth() {
    const dispatch = useDispatch()

    const navigate = useNavigate()

    const query = useQuery()

    const { token, signedIn } = useSelector((state) => state.auth.session)

    const signIn = async (values) => {
        try {
            const resp = await apiSignIn(values)
            if (resp.data && resp.data.user) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))
                dispatch(
                    setUser(
                        resp.data.user || {
                            avatar: resp?.data?.user?.user_photo,
                            userName: resp?.data?.user?.user_name,
                            userFullName: resp?.data?.user?.user_fullname,
                            project_title: resp?.data?.user?.project_title,
                            project_id: resp?.data?.user?.project_id,
                            authority: [resp?.data?.user?.user_rank],
                            email: resp?.data?.user?.user_mail,
                            permissions: [resp?.data?.user?.permissions]
                        }
                    )
                )

                if(resp.data.user.authority=='SUPERADMIN'){
                    const redirectUrl = query.get(REDIRECT_URL_KEY)
                    navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath)
                } else{
                    navigate(appConfig.selectProject)
                }

                return {
                    status: 'success',
                    message: 'Giriş yapılıyor.',
                }
            } else {
                // Servisten başarılı response geldi ama user bilgisi yok (hatalı giriş)
                return {
                    status: 'failed',
                    message: resp.data?.message || 'Giriş başarısız.',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const signUp = async (values) => {
        try {
            const resp = await apiSignUp(values)
            if (resp.data && resp.data.user) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))
                dispatch(
                    setUser(
                        resp.data.user || {
                            avatar: resp?.data?.user?.user_photo,
                            userName: resp?.data?.user?.user_name,
                            userFullName: resp?.data?.user?.user_fullname,
                            project_title: resp?.data?.user?.project_title,
                            project_id: resp?.data?.user?.project_id,
                            authority: [resp?.data?.user?.authority],
                            email: resp?.data?.user?.user_mail,
                            permissions: [resp?.data?.user?.permissions]
                        }
                    )
                )

                if(resp?.data?.user?.authority=='SUPERADMIN'){
                    const redirectUrl = query.get(REDIRECT_URL_KEY)
                    navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath)
                } else{
                    const redirectUrl = query.get(REDIRECT_URL_KEY)
                    navigate(redirectUrl ? redirectUrl : appConfig.selectProject)
                }

                return {
                    status: 'success',
                    message: 'Giriş yapılıyor.',
                }
            } else {
                // Servisten başarılı response geldi ama user bilgisi yok (hatalı kayıt)
                return {
                    status: 'failed',
                    message: resp.data?.message || 'Kayıt başarısız.',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const inviteSignUp = async (values) => {
        try {
            const resp = await apiInviteSignUp(values)
            if (resp.data && resp.data.user) {
                const { token } = resp.data
                dispatch(onSignInSuccess(token))
                dispatch(
                    setUser(
                        resp.data.user || {
                            avatar: resp?.data?.user?.user_photo,
                            userName: resp?.data?.user?.user_name,
                            userFullName: resp?.data?.user?.user_fullname,
                            project_title: resp?.data?.user?.project_title,
                            project_id: resp?.data?.user?.project_id,
                            authority: [resp?.data?.user?.authority],
                            email: resp?.data?.user?.user_mail,
                            permissions: [resp?.data?.user?.permissions]
                        }
                    )
                )

                // Davet ile kayıt olan kullanıcılar direkt projeye yönlendirilir
                navigate(appConfig.authenticatedEntryPath)

                return {
                    status: 'success',
                    message: 'Kayıt başarılı, yönlendiriliyorsunuz.',
                }
            } else {
                // Servisten başarılı response geldi ama user bilgisi yok (hatalı kayıt)
                return {
                    status: 'failed',
                    message: resp.data?.message || 'Kayıt başarısız.',
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const handleSignOut = () => {
        dispatch(onSignOutSuccess())
        dispatch(setUser(initialState))
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        await apiSignOut()
        handleSignOut()
    }

    return {
        authenticated: token && signedIn,
        signIn,
        signUp,
        inviteSignUp,
        signOut,
    }
}

export default useAuth
